import { supabase } from "@/integrations/supabase/client";
import { Campaign } from "@/lib/queries/filtered-opportunities";
import { NpoProfile, Opportunities, OpportunityCatalog, OpportunityCatalogImages } from "@/integrations/supabase/types/opportunities";

type ApiNpoProfile = {
  org_name: string;
  website: string | null;
  logo_url: string | null;
  description: string | null;
  focus_areas: string[];
  sdg_alignment: string[];
  city: string;
  country: string;
  beneficiary_types: string[];
  meta?: Record<string, any>;
}

type CatalogEntry = Omit<OpportunityCatalog["Row"], "opportunity" | "npo"> & {
  opportunity_catalog_images: OpportunityCatalogImages["Row"][];
  npo: ApiNpoProfile;
  format: string;
  format_details: string;
  duration: string;
  max_volunteers: number;
  host_included: boolean;
  opportunity: Opportunities["Row"];
  images: OpportunityCatalogImages["Row"][];
}

interface ApiResponse {
  data: {
    campaigns: Campaign[];
    total: number;
    npo: ApiNpoProfile;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  message: string;
}

export const getOpportunityByCatalogSlug = async (catalogSlug: string, npoSlug: string) => {
  const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/campaign/opportunities/npo/${npoSlug}/${catalogSlug}`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { data } = await response.json();
  return data as CatalogEntry;
};

export const getOpportunityById = async (id: string) => {
  const { data, error } = await supabase
    .from("opportunities")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data as unknown as Opportunities["Row"];
};

export const getOpportunityByCatalogId = async (catalogId: string) => {
  const { data, error } = await supabase
    .from("opportunity_catalog")
    .select(`
      *,
      opportunity:opportunities(*),
      npo:npo_profiles(*),
      images:opportunity_catalog_images(*)
    `)
    .eq("id", catalogId)
    .single();

  if (error) throw error;
  return data as unknown as CatalogEntry;
};

export const getRelatedNPOOpportunities = async (npoId: string, excludeSlug: string, limit = 4) => {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/opportunities/npo/${npoId}?exclude=${excludeSlug}&limit=${limit}`
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { data } = await response.json() as ApiResponse;
  return data.campaigns;
};

export const getSimilarOpportunities = async (npoId: string, limit = 4) => {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/opportunities/npo/${npoId}?similar&limit=${limit}`
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { data } = await response.json() as ApiResponse;
  return data.campaigns;
};